import { Fragment, useEffect, useMemo } from 'react';

import clsx from 'clsx';
import { Controller, useFormContext } from 'react-hook-form';

import { WrappedInput, WrappedSelect } from '@quick/components/Forms';
import { Switch } from '@quick/components/Switch';
import type { UnitTypesRes } from '@quick/go-api-types/money/unit-types';

import { useAppTranslation } from '@/app/i18n/client';
import { useCurrencyUtils } from '@/layers/core/deal/services/useCurrencyUtils';
import { useFetch } from '@/layers/core/deal/services/useFetch';

import { TEXT_FIELD_ID } from './constants';
import { useFormDataContext } from './FormDataContext';
import type { IForm } from './types';
import { sortAdditionalFields, stripNumericalUnitSymbols } from './utils';

export function AdditionalForm() {
  const { t } = useAppTranslation(['common', 'demand']);
  const { control, register, watch } = useFormContext<IForm>();
  const { limitToMinValue, limitToMaxValue, cleanValue, formatValue } =
    useCurrencyUtils();

  const { serviceType } = useFormDataContext();

  const loadType = watch('cargo_type_id');

  const [fields, checkbox, select, text] = useMemo(() => {
    const forOffer = serviceType?.additional_fields?.filter(
      ({ field: { is_for_offer, is_package } }) => is_for_offer && !is_package
    );
    const fields =
      forOffer
        ?.filter(({ field: { field_type } }) => field_type?.id === 1)
        ?.sort(sortAdditionalFields) ?? [];
    const checkbox =
      forOffer
        ?.filter(({ field: { field_type } }) => field_type?.id === 2)
        ?.sort(sortAdditionalFields) ?? [];
    const select =
      forOffer
        ?.filter(({ field: { field_type } }) => field_type?.id === 3)
        ?.sort(sortAdditionalFields) ?? [];
    const text =
      forOffer
        ?.filter(({ field: { field_type } }) => field_type?.id === 4)
        ?.sort(sortAdditionalFields) ?? [];

    return [fields, checkbox, select, text];
  }, [serviceType]);
  const length =
    fields?.length + checkbox?.length + select.length + text?.length;
  if (!length || !loadType) return null;
  return (
    <div className="grid grid-cols-[repeat(auto-fit,minmax(9rem,1fr))] gap-4 sm:col-span-2">
      {fields.map((data) => (
        <Fragment key={data.field.id}>
          {data.field.magnitude.dimensions.map((d) => (
            <Controller
              key={d}
              name={`additional_fields.${data.field.id.toString()}_.dimensions.${d}`}
              control={control}
              rules={{
                required: data.required && t('common:msg.required_field'),
                validate: (v) =>
                  data.required && !Number(v)
                    ? t('common:msg.required_field')
                    : undefined,
              }}
              shouldUnregister
              defaultValue={0 as unknown as undefined} // Sorry again. Type unions
              render={({ field }) => (
                <WrappedInput
                  {...field}
                  isOnModal
                  autoComplete="off"
                  className="w-full"
                  wrappedClassName="h-[42px]"
                  requiredMark={data.required}
                  value={field.value ? formatValue(`${field.value}`) : '0'}
                  onChange={(e) => {
                    let newAditional_fields = limitToMaxValue(
                      cleanValue(e.target.value),
                      9_999_999
                    );
                    newAditional_fields = limitToMinValue(
                      newAditional_fields,
                      0
                    );
                    field.onChange(newAditional_fields);
                  }}
                  label={
                    d === 'Quantity'
                      ? data.field.name
                      : data.field.name === 'Weight'
                        ? t(`demand:units.${data.field.name as 'Weight'}`)
                        : `${t(`demand:units.${d as 'Height'}`, {
                            defaultValue: d,
                          })} (${t(
                            `demand:units.${data.field.name as 'Height'}`,
                            {
                              defaultValue: data.field.name,
                            }
                          )})`
                  }
                  // error={} In theory, this should not error
                />
              )}
            />
          ))}
          <Unit id={+data.field.id} magnitude={data.field.magnitude.id} />
          <Total
            id={+data.field.id}
            name={data.field.name}
            magnitude={data.field.magnitude.id}
          />
        </Fragment>
      ))}
      {select.map(
        (
          {
            required,
            field: {
              id,
              goods: { types },
              name,
            },
          },
          i
        ) => (
          <Controller
            key={id}
            name={`additional_fields.${id}_`}
            control={control}
            shouldUnregister
            rules={{
              required: required && t('common:msg.required_field'),
            }}
            render={({ field, fieldState: { error } }) => (
              <WrappedSelect
                {...field}
                isOnModal
                value={(field.value as string) ?? ''}
                label={name}
                requiredMark={required}
                error={error?.message && ' '}
                className={clsx(i === 0 && 'col-start-1')}
                wrappedClassName="h-[42px]"
              >
                <option value="" hidden>
                  {t('common:select_option')}:
                </option>
                {types.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </WrappedSelect>
            )}
          />
        )
      )}
      {text.map(({ required, field: { id, name } }, i) => (
        <Controller
          key={id}
          name={`additional_fields.${id}_${TEXT_FIELD_ID}`}
          control={control}
          shouldUnregister
          rules={{
            required: required && t('common:msg.required_field'),
            maxLength: {
              value: 100,
              message: t('common:msg.max_chars_with_var', { max: 100 }),
            },
            validate: (v) => {
              if (
                name === 'nmfc' &&
                v &&
                !/^[0-9]{6}-[0-9]{2}$/.test(v as string)
              )
                return t('common:msg.format', {
                  format: 'NNNNNN-NN',
                  example: '123456-78',
                });
            },
          }}
          render={({ field, fieldState: { error } }) => (
            <WrappedInput
              {...field}
              isOnModal
              value={(field.value as string) ?? ''}
              label={name}
              requiredMark={required}
              error={error?.message && ' '}
              wrappedClassName="h-[42px]"
              className={clsx(i === 0 && 'col-start-1')}
            />
          )}
        />
      ))}

      {checkbox.map((data, i) => (
        <Switch
          key={`${data.field.field_filter}_${data.field.name}`}
          {...register(`additional_fields.${data.field.id}_`, {
            shouldUnregister: true,
          })}
          className={clsx(
            'flex w-full items-center gap-2',
            i === 0 && 'col-start-1'
          )}
        >
          <span
            className="flex h-full w-4/5 break-before-auto items-center text-xs font-semibold
              dark:text-white"
          >
            {data.field.name}
          </span>
        </Switch>
      ))}
    </div>
  );
}

function Unit({ id, magnitude }: { id: number; magnitude: number }) {
  const { data } = useFetch<UnitTypesRes>({
    url: '/money/unit-types',
    params: { magnitude, pagination: false },
  });
  const { country_id } = useFormDataContext();
  const defaultUnit =
    (country_id === 4 &&
      data?.find(({ unit_symbol }) => /lb|in/.test(unit_symbol))) ||
    data?.find(({ default: def }) => def);

  const { setValue, getValues, control } = useFormContext<IForm>();

  useEffect(() => {
    if (!defaultUnit) return;
    const fieldName = `additional_fields.${id}_.unit_type` as const;
    const unit = getValues(fieldName);
    if (!unit) setValue(fieldName, defaultUnit.id, { shouldDirty: true });
  }, [defaultUnit, id, getValues, setValue]);

  return (
    <Controller
      key={defaultUnit ? 'y' : 'n'}
      name={`additional_fields.${id}_.unit_type`}
      control={control}
      shouldUnregister
      render={({ field: f }) => (
        <WrappedSelect
          {...f}
          isOnModal
          className={clsx(defaultUnit?.unit_symbol === '#' && 'hidden')}
          wrappedClassName="h-[42px]"
        >
          {data?.map(({ id, unit_symbol }) => (
            <option value={id} key={id}>
              {stripNumericalUnitSymbols(unit_symbol)}
            </option>
          ))}
        </WrappedSelect>
      )}
    />
  );
}

function Total({
  id,
  name,
  magnitude,
}: {
  id: number;
  name: string;
  magnitude: number;
}) {
  const { data } = useFetch<UnitTypesRes>({
    url: '/money/unit-types',
    params: { magnitude, pagination: false },
  });

  const defaultUnit = data?.find(({ default: def }) => def);

  const factors = useMemo(() => {
    return Object.values(data ?? {})?.reduce(
      (p, v) => {
        p[v.id] = Number(v.conversion_factor);
        return p;
      },
      {} as Record<string, number>
    );
  }, [data]);

  const { setValue, watch, control } = useFormContext<IForm>();
  useEffect(() => {
    const matcher = new RegExp(`additional_fields.${id}_`);
    const s = watch((status, change) => {
      if (change.name?.includes('total')) return;
      if (!matcher.exec(change.name ?? '')) return;
      const field = status.additional_fields?.[`${id}_`];
      if (typeof field === 'boolean' || typeof field === 'string') return;
      const dimensions = Object.values(field?.dimensions ?? {});
      const unit = `${field?.unit_type}`;
      const factor = factors?.[unit ?? ''] ?? 1;
      const total = dimensions.reduce((p, v) => p || 1 * Number(v), 0);
      setValue(`additional_fields.${id}_.total`, (total || 0) * factor);
    });
    return () => s.unsubscribe();
  }, [id, factors, setValue, watch]);

  const { locale } = useCurrencyUtils();

  return (
    <Controller
      name={`additional_fields.${id}_.total`}
      control={control}
      defaultValue={0 as unknown as undefined} // Sorry again. Type unions
      shouldUnregister
      render={({ field }) => (
        <WrappedInput
          label={name}
          {...field}
          isOnModal
          className={clsx(defaultUnit?.unit_symbol === '#' && 'hidden')}
          value={
            field.value &&
            Number(`${field.value}`).toLocaleString(locale, {
              maximumSignificantDigits: 2,
              maximumFractionDigits: 2,
              trailingZeroDisplay: 'stripIfInteger',
              roundingPriority: 'morePrecision',
            })
          }
          wrappedClassName="h-[42px]"
          readOnly
          symbolRight
          tabIndex={-1}
          symbol={defaultUnit?.unit_symbol
            .replace('^2', '²')
            .replace('^3', '³')}
        />
      )}
    />
  );
}
